<template>
    <page-layout :readonly="isProposalAccepted" :hide-footer="isProposalAccepted">
        <template #header>
            <proposal-step-header
                :current-step="stepNumber"
                :total-steps="totalSteps"
                title="Shipping Information"
                :use-exit-modal="!isProposalAccepted && exitConfirmationRequired"
            />
        </template>
        <template #default>
            <g-loader v-if="isLoading" />
            <template v-else>
                <div class="margin-bottom-large">
                    <g-typography tag="p" :value="shippingDescription" class="break-new-line" />
                </div>
                <template v-if="hasCompensationMethod('product')">
                    <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />
                    <proposal-step-error
                        v-if="proposalStep && proposalStep.error"
                        class="margin-bottom-normal"
                        :step="proposalStep"
                    />
                    <g-form>
                        <g-form-item :required="true" label="Email" :errors="errors.email">
                            <g-input v-model="address.email" size="large" />
                        </g-form-item>
                        <g-form-item :required="true" label="First Name" :errors="errors.first_name">
                            <g-input v-model="address.first_name" size="large" />
                        </g-form-item>
                        <g-form-item :required="true" label="Last Name" :errors="errors.last_name">
                            <g-input v-model="address.last_name" size="large" />
                        </g-form-item>
                        <g-form-item :required="true" label="Address" :errors="errors.street">
                            <g-address-validation
                                v-if="featureResponsiveDesign && googlePlacesApiKey && !googlePlacesError"
                                class="notranslate address-validation"
                                :api-key="googlePlacesApiKey"
                                :default-value="address.street"
                                size="large"
                                @change="onPlaceChange"
                            />
                            <div v-else class="fallback-address-input">
                                <g-input v-model="address.street" size="large" />
                                <g-typography
                                    v-if="googlePlacesError"
                                    class="g-mt-3"
                                    tag="p"
                                    value="We couldn't verify this address, so please make sure it's correct."
                                    color="error"
                                />
                            </div>
                        </g-form-item>
                        <g-form-item :required="false" label="Apt / Suite" :errors="errors.street_2">
                            <g-input v-model="address.street_2" size="large" />
                        </g-form-item>
                        <g-form-item :required="true" label="Country" :errors="errors.country">
                            <g-select
                                v-model="address.country"
                                class="notranslate"
                                show-search
                                option-filter-prop="children"
                                :loading="loadingCountries"
                                :filter-option="filterLocation"
                                size="large"
                                style="width: 100%"
                                @change="
                                    () => {
                                        address.state = null;
                                        address.state_code = null;
                                        address.country_code = null;
                                    }
                                "
                            >
                                <g-select-option
                                    v-for="country in countries"
                                    :key="country.id"
                                    :value="country.name"
                                    class="notranslate"
                                >
                                    {{ country.name }}
                                </g-select-option>
                            </g-select>
                        </g-form-item>
                        <g-form-item :required="true" label="City" :errors="errors.city">
                            <g-input v-model="address.city" size="large" />
                        </g-form-item>
                        <g-form-item
                            v-if="provinces.length > 0"
                            :required="true"
                            :label="stateLabel"
                            :errors="errors.state"
                        >
                            <g-select
                                v-model="address.state"
                                class="notranslate"
                                show-search
                                option-filter-prop="children"
                                :loading="loadingProvinces"
                                :filter-option="filterLocation"
                                size="large"
                                style="width: 100%"
                                @change="
                                    () => {
                                        address.state_code = null;
                                    }
                                "
                            >
                                <g-select-option
                                    v-for="province in provinces"
                                    :key="province.id"
                                    :value="province.name"
                                    class="notranslate"
                                >
                                    {{ province.name }}
                                </g-select-option>
                            </g-select>
                        </g-form-item>
                        <g-form-item :required="true" :label="zipLabel" :errors="errors.zip">
                            <g-input v-model="address.zip" size="large" />
                        </g-form-item>
                    </g-form>
                </template>
            </template>
        </template>
        <template #footer>
            <proposal-step-footer
                :is-next-disabled="isDisabled"
                :loading="isCompletingProposal"
                :step-completed="isCompleted"
                :current-step="stepNumber"
                :total-steps="totalSteps"
                :use-exit-modal="!isProposalAccepted && exitConfirmationRequired"
                @go-next-step="goNextStep"
                @go-previous-step="$emit('go-previous-step', proposalStep)"
            />
        </template>
    </page-layout>
</template>

<script>
    import * as _ from 'lodash';
    import PageLayout from '../../layouts/PageLayout';
    import ProposalStepHeader from './partials/ProposalStepHeader';
    import ProposalStepFooter from './partials/ProposalStepFooter';
    import LinkCodeBox from './partials/LinkCodeBox';
    import ErrorMessageCard from './partials/ErrorMessageCard';
    import ProposalStepError from './partials/PoposalStepError';
    import ProposalMixin from './mixins/ProposalMixin';
    import ExitConfirmationMixin from './mixins/ExitConfirmationMixin';
    import useFeatureFlagsStore from '../../../../stores/feature-flags';
    import useAddress from '../../../../src/composables/use-address';

    export default {
        name: 'ProposalShippingInfoView',
        components: {
            ProposalStepError,
            ErrorMessageCard,
            LinkCodeBox,
            PageLayout,
            ProposalStepHeader,
            ProposalStepFooter
        },
        mixins: [ProposalMixin, ExitConfirmationMixin],
        props: {
            campaign: {
                type: Object
            },
            proposal: {
                type: Object
            },
            contact: {
                type: Object
            },
            proposalStep: {
                type: Object
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            isCompletingProposal: {
                type: Boolean,
                default: false
            },
            countries: {
                type: Array,
                required: true
            },
            provinces: {
                type: Array,
                required: false,
                default: () => []
            },
            loadingCountries: {
                type: Boolean,
                default: false
            },
            loadingProvinces: {
                type: Boolean,
                default: false
            },
            errors: {
                type: Object | Array,
                required: false,
                default: () => {}
            },
            errorMessage: {
                type: String,
                required: false
            }
        },
        data() {
            const flags = useFeatureFlagsStore().data;
            return {
                form: null,
                address: {
                    email: null,
                    street: null,
                    street_2: null,
                    country: null,
                    country_code: null,
                    city: null,
                    state: null,
                    state_code: null,
                    zip: null,
                    first_name: null,
                    last_name: null
                },
                initialAddress: null,

                featureResponsiveDesign: flags['live-site-responsive-design'] || false,
                googlePlacesApiKey: window.grinEnv.GOOGLE_PLACES_API_KEY || false,
                googlePlacesError: false
            };
        },
        computed: {
            shippingDescription() {
                const description = _.get(this, 'campaign.customization.shipping_address_step_overview');

                return description
                    ? description
                    : 'This is where we will send your products. Please make sure this is up to date.';
            },
            isDisabled() {
                //TODO: replace with Ant form validation if possible
                return !(
                    this.address.email &&
                    this.address.street &&
                    this.address.country &&
                    this.address.city &&
                    (this.provinces.length > 0 ? this.address.state : true) &&
                    (this.isZipRequired ? this.address.zip : true) &&
                    this.address.first_name &&
                    this.address.last_name
                );
            },
            stepNumber() {
                return _.get(this, 'proposalStep.meta.stepNumber', 0);
            },
            totalSteps() {
                return _.get(this, 'proposalStep.meta.totalSteps', 0);
            },
            stateLabel() {
                return this.selectedCountryData && this.selectedCountryData.province_label
                    ? this.selectedCountryData.province_label
                    : 'Province';
            },
            selectedCountryData() {
                if (!this.address.country) {
                    return null;
                }

                return this.countries.find((country) => country.name === this.address.country);
            },
            selectedStateData() {
                if (!this.address.state) {
                    return null;
                }

                return this.provinces.find((state) => state.name === this.address.state);
            },
            zipLabel() {
                return this.address.country && this.address.country === 'United States' ? 'Zipcode' : 'Postcode';
            },
            isZipRequired() {
                return this.selectedCountryData ? this.selectedCountryData.has_postal_code : true;
            },
            isCompleted() {
                return _.get(this, 'proposalStep.is_complete', false);
            },
            initialStepState() {
                return this.initialAddress;
            },
            currentStepState() {
                return this.address;
            }
        },
        methods: {
            hasCompensationMethod(method) {
                return _.get(this, 'campaign.compensation', []).includes(method);
            },
            goNextStep() {
                const countryCode = this.address.country_code;
                const stateCode = this.address.state_code;
                const payload = {
                    ...this.address,
                    country_code:
                        !countryCode && this.selectedCountryData ? this.selectedCountryData.code : countryCode,
                    state_code: !stateCode && this.selectedStateData ? this.selectedStateData.code : stateCode,
                };
                this.$emit('go-next-step', this.proposalStep, payload);
            },
            prePopulateAddressFromContact() {
                if (this.contact && this.contact.primary_address) {
                    this.$emit('country-updated', this.contact.primary_address.country);

                    this.address = _.pick(this.contact.primary_address, Object.keys(this.address));

                    this.updateInitialState();
                }
            },
            filterLocation(input, option) {
                return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            },
            updateInitialState() {
                this.initialAddress = _.cloneDeep(this.address);
            },

            /**
             * Handle the Google place change when an address
             * suggestion is selected using address validation
             */
            onPlaceChange(addressParts) {
                if (!addressParts) {
                    return;
                }

                if (useAddress().isGooglePlaceAutoCompleteSuggestionMissingStreetNumber(addressParts)) {
                    this.googlePlacesError = true;
                }

                this.address.street = addressParts.street;
                this.address.city = addressParts.city;
                this.address.state = addressParts.state;
                this.address.state_code = addressParts.state_code;
                this.address.zip = addressParts.zip;
                this.address.country = addressParts.country;
                this.address.country_code = addressParts.country_code;
            }
        },
        watch: {
            address: {
                deep: true,
                handler(value, previous) {
                    this.$emit('country-updated', value.country);
                }
            },
            contact() {
                this.prePopulateAddressFromContact();
            }
        },
        created() {
            this.prePopulateAddressFromContact();
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    .ant-form-item {
        margin-bottom: 0;
    }

    .break-new-line {
        white-space: pre-line;
    }
</style>
